<script setup lang="ts">
import { computed } from 'vue';
import { CardProgressState } from '../card.constants';
import ProgressBar from '../../progress/progress-bar/progress-bar.vue';
import { ProgressColor } from '../../progress/progress.constants';
import Icon from '../../icon/icon.vue';
import { IconName, IconSize } from '../../icon/icon.constants';

type Props = {
  /** Learning state, driving the bar color and how much of it is filled. */
  state: CardProgressState;
  /** Completion percentage. Only read in the in-progress state. */
  progress?: number;
  /** Caption below the bar, e.g. "Keep going!". */
  message?: string;
  /** Glyph beside the caption. Defaults to the glyph for `state`. */
  iconName?: IconName;
  /**
   * Whether to show the numeric percentage beside the bar. Defaults to on for
   * the in-progress state only, matching the design.
   */
  showValue?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  progress: 0,
  // Vue casts an absent optional boolean to `false`, which would defeat the
  // state-derived fallback below; an explicit default keeps it `undefined`.
  showValue: undefined,
});

const stateColor: Record<CardProgressState, ProgressColor> = {
  [CardProgressState.NOT_STARTED]: ProgressColor.NEUTRAL,
  [CardProgressState.IN_PROGRESS]: ProgressColor.PRIMARY,
  [CardProgressState.IN_REVIEW]: ProgressColor.PRIMARY,
  [CardProgressState.COMPLETED]: ProgressColor.SUCCESS,
  [CardProgressState.FAILED]: ProgressColor.ERROR,
};

const stateIcon: Record<CardProgressState, IconName> = {
  [CardProgressState.NOT_STARTED]: IconName.STAR,
  [CardProgressState.IN_PROGRESS]: IconName.STAR,
  [CardProgressState.IN_REVIEW]: IconName.INFO_CIRCLE,
  [CardProgressState.COMPLETED]: IconName.CHECK_CIRCLE,
  [CardProgressState.FAILED]: IconName.ERROR_CIRCLE,
};

const messageColorClass: Record<CardProgressState, string> = {
  [CardProgressState.NOT_STARTED]: 'text-on-surface-secondary',
  [CardProgressState.IN_PROGRESS]: 'text-on-surface-secondary',
  [CardProgressState.IN_REVIEW]: 'text-on-surface-secondary',
  [CardProgressState.COMPLETED]: 'text-success',
  [CardProgressState.FAILED]: 'text-error',
};

// Every terminal state fills the bar; only an in-progress card shows partial
// completion.
const barProgress = computed(() =>
  props.state === CardProgressState.IN_PROGRESS ? props.progress : 100,
);

const isValueVisible = computed(
  () => props.showValue ?? props.state === CardProgressState.IN_PROGRESS,
);

const clampedProgress = computed(() =>
  Math.round(Math.min(Math.max(props.progress, 0), 100)),
);
</script>

<template>
  <div class="flex w-full flex-col gap-sm">
    <div class="flex items-center gap-md">
      <ProgressBar :progress="barProgress" :color="stateColor[props.state]" />
      <span
        v-if="isValueVisible"
        class="text-caption shrink-0 font-bold text-on-surface-primary"
      >
        {{ clampedProgress }}%
      </span>
    </div>
    <p
      v-if="props.message || $slots.default"
      class="text-caption flex items-center gap-sm"
      :class="messageColorClass[props.state]"
    >
      <slot name="icon">
        <Icon
          :name="props.iconName ?? stateIcon[props.state]"
          :size="IconSize.SM"
          class="shrink-0"
        />
      </slot>
      <slot>{{ props.message }}</slot>
    </p>
  </div>
</template>
